import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import api from '../utils/axiosConfig';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

/** Set on explicit logout; Login uses this to open dashboard instead of restoring map/deep links. */
export const POST_LOGOUT_PREFER_DASHBOARD_KEY = 'postLogoutPreferDashboard';

export const AuthProvider = ({ children }) => {
  const LAST_VISITED_ROUTE_KEY = 'lastVisitedRoute';
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  // Logout function
  const logout = useCallback(async () => {
    const currentToken = token;
    try {
      if (currentToken) {
        await api.post('/logout');
      }
    } catch (error) {
      const status = error?.response?.status;

      if (status === 401 || status === 403) {
        console.warn('Logout API call returned status', status, '- proceeding with local cleanup.');
      } else {
        console.error('Logout API call failed:', error);
      }
    } finally {
      // Always clear local state
      setUser(null);
      setToken(null);
      localStorage.removeItem('jwtToken');
      localStorage.removeItem('user');
      localStorage.removeItem('loggedAs');
      localStorage.removeItem('userId');
      localStorage.removeItem('userName');
      localStorage.removeItem(LAST_VISITED_ROUTE_KEY);
      try {
        sessionStorage.setItem(POST_LOGOUT_PREFER_DASHBOARD_KEY, '1');
      } catch (_) {}

      // Remove axios default header
      delete axios.defaults.headers.common['Authorization'];
    }
  }, [token]);

  // Initialize auth state from localStorage
  useEffect(() => {
    const storedToken = localStorage.getItem('jwtToken');
    const storedUser = localStorage.getItem('user');
    
    if (storedToken && storedUser) {
      try {
        const userData = JSON.parse(storedUser);
        setToken(storedToken);
        setUser(userData);
        
        // Set axios default header
        axios.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;
      } catch (error) {
        console.error('Error parsing stored user data:', error);
        logout();
      }
    }
    setLoading(false);
  }, [logout]);

  // Login function
  const login = (userData, token) => {
    setUser(userData);
    setToken(token);
    localStorage.setItem('jwtToken', token);
    localStorage.setItem('user', JSON.stringify(userData));
    
    // Set axios default header
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  };

  // Check if user is authenticated
  const isAuthenticated = () => {
    return !!token && !!user;
  };

  // Check if user has specific role
  const hasRole = (role) => {
    return user && user.role === role;
  };

  // Check if user has any of the specified roles
  const hasAnyRole = (roles) => {
    return user && roles.includes(user.role);
  };

  const value = {
    user,
    token,
    loading,
    login,
    logout,
    isAuthenticated,
    hasRole,
    hasAnyRole
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
